export interface Experience {
  id: string;
  title: string;
  company: string;
  duration: string;
  description: string;
  technologies: string[];
  current?: boolean;
}

export interface Project {
  id: string;
  title: string;
  description: string;
  detailedDescription?: string;
  image: string;
  technologies: string[];
  githubUrl?: string;
  liveUrl?: string;
  featured?: boolean;
  caseStudy?: {
    challenge: string;
    solution: string;
    results: string[];
    impact: string;
  };
  metrics?: {
    downloads?: string;
    stars?: string;
    version?: string;
    users?: string;
  };
}

export interface Skill {
  name: string;
  level: number;
  category: 'frontend' | 'backend' | 'testing' | 'tools' | 'mobile';
}

export interface Certification {
  name: string;
  issuer: string;
  date: string;
  credentialId?: string;
  url?: string;
}

export interface ContactInfo {
  email: string;
  phone?: string;
  location: string;
  linkedin: string;
  github?: string;
  calendly?: string;
}

export interface App {
  id: string;
  name: string;
  description: string;
  platform: string;
  technologies: string[];
  status: string;
  downloadUrl: string | null;
  iosUrl?: string | null;
  androidUrl?: string | null;
  detailPath?: string | null;
  supportPath?: string | null;
  icon: string;
  features: string[];
  detailedInfo?: {
    playStoreUrl: string;
    version: string;
    downloads: string;
    rating: string;
    contentRating: string;
    category: string;
    developer: string;
    supportEmail: string;
    phoneNumber?: string;
    keyFeatures: Array<{
      title: string;
      description: string;
      icon: string;
    }>;
    premiumFeatures: string[];
    benefits: string[];
    privacy: string[];
    screenshots: string[];
  };
}

export interface Hobby {
  id: string;
  name: string;
  description: string;
  icon: string;
  technologies: string[];
  projects: string[];
}

export interface MediumArticle {
  id: string;
  title: string;
  description: string;
  url: string;
  publishedDate: string;
  readTime: string;
  tags: string[];
  featured?: boolean;
}

export const experiences: Experience[] = [
  {
    id: '1',
    title: 'Platform Automation Lead',
    company: 'Exinity UAE',
    duration: 'Jan 2025 – Present',
    description: 'Leading the design and development of robust automation frameworks from scratch using Playwright and Appium, tailored to diverse web and mobile tech stacks across multiple teams. Building and scaling QA teams from the ground up, providing mentorship, defining best practices, and aligning them with product and engineering goals. Establishing and standardizing end-to-end QA processes, ensuring quality gates are embedded seamlessly into fast-paced agile development cycles.',
    technologies: ['Playwright', 'Appium', 'Automation Framework', 'Team Leadership', 'CI/CD', 'Agile', 'Scrum', 'Quality Assurance'],
    current: true
  },
  {
    id: '2',
    title: 'Automation Test Engineer',
    company: 'M2, Dubai',
    duration: 'Jan 2023 – Jan 2025',
    description: 'Built QA teams from scratch for multiple teams with different tech stacks. Developed and maintained solid test automation scripts using Cypress and Appium. Created automation frameworks from scratch based on requirements and implemented QA processes aligned with engineering teams. Maintained CI/CD pipelines to run test automation as part of day-to-day processes.',
    technologies: ['Cypress', 'Appium', 'Automation Framework', 'CI/CD', 'Bitbucket Pipeline', 'Test Automation', 'Agile', 'Scrum'],
    current: false
  },
  {
    id: '3',
    title: 'Senior QA Automation Test Engineer',
    company: 'Netcoins Inc., Canada',
    duration: 'Sept 2022 – Dec 2022',
    description: 'Developed and maintained web UI and API automation test scripts using Cypress. Set up workflows using Bitbucket pipeline to provide automatic test process and reporting. Created automation frameworks from scratch based on requirements and conducted case studies on projects to provide feedback on improvements. Worked with Agile Methodology and Scrum for all projects.',
    technologies: ['Cypress', 'API Testing', 'Bitbucket Pipeline', 'Automation Framework', 'Agile', 'Scrum', 'Test Reporting'],
    current: false
  },
  {
    id: '4',
    title: 'QA Automation Test Engineer',
    company: 'Rain Technology Services, Dubai, UAE',
    duration: 'Dec 2021 – Sept 2022',
    description: 'Built and implemented solid and scalable automation frameworks for Web, Mobile and API testing. Developed and maintained web UI and API automation test scripts using Cypress, and mobile UI automation test scripts using Espresso and XCUITest. Set up workflows using GitHub Actions to provide automatic test process and reporting, and configured cloud testing using BrowserStack cloud farm.',
    technologies: ['Cypress', 'Espresso', 'XCUITest', 'GitHub Actions', 'BrowserStack', 'API Testing', 'Mobile Testing', 'Web Testing'],
    current: false
  },
  {
    id: '5',
    title: 'Senior Software Test Engineer',
    company: 'Tribalscale/Bank Saudi Fransi, Dubai, UAE',
    duration: 'Sept 2019 – Dec 2021',
    description: 'Created automation frameworks from scratch based on requirements and developed comprehensive test plans to fit QA requirements into projects. Improved client processes to increase product quality and led teams on automation testing while assisting with project inputs. Helped teams troubleshoot automation testing issues and interacted with clients to improve QA processes. Conducted case studies on projects and provided feedback on improvements.',
    technologies: ['Automation Framework', 'Test Planning', 'Process Improvement', 'Team Leadership', 'Client Management', 'Agile', 'Scrum'],
    current: false
  },
  {
    id: '6',
    title: 'Senior Software Test Engineer',
    company: 'Unique Computer Systems, Sharjah, UAE',
    duration: 'Aug 2016 – Aug 2019',
    description: 'Created comprehensive test plans, test processes, test cases and test data. Developed automation test scripts using Selenium and Appium with TestNG & JUnit framework to perform regression testing. Designed and developed test procedures based on requirements and worked with Agile Methodology and Scrum for all projects. Involved in Smoke Testing, Functional Testing, Regression Testing and Exploratory Testing.',
    technologies: ['Selenium', 'Appium', 'TestNG', 'JUnit', 'Regression Testing', 'Agile', 'Scrum', 'Functional Testing'],
    current: false
  },
  {
    id: '7',
    title: 'Software Engineer',
    company: 'Zydesoft Solutions, Chennai, India',
    duration: 'Nov 2015 – Aug 2016',
    description: 'Involved in Smoke Testing, Functional Testing, Regression Testing and Exploratory Testing for mobile native applications. Created test scenarios and test cases, and developed automation test scripts using Selenium and Appium framework with TestNG. Managed defect tracking in JIRA and performed defect revalidations.',
    technologies: ['Selenium', 'Appium', 'TestNG', 'JIRA', 'Mobile Testing', 'Functional Testing', 'Regression Testing'],
    current: false
  },
  {
    id: '8',
    title: 'Test Engineer',
    company: 'GBS India Pvt. Ltd., Chennai, India',
    duration: 'Jun 2015 – Nov 2015',
    description: 'Helped teams troubleshoot automation testing issues and interacted with clients to improve QA processes. Conducted case studies on projects and provided feedback on improvements. Worked with Agile Methodology and Scrum for all projects.',
    technologies: ['Automation Testing', 'Client Management', 'Process Improvement', 'Agile', 'Scrum', 'Troubleshooting'],
    current: false
  }
];

export const projects: Project[] = [
  // Featured NPM Packages
  {
    id: '1',
    title: 'Super Pancake Automation',
    description: 'A lightweight DOM-based UI automation framework using Chrome DevTools Protocol. Latest version 2.10.2 with comprehensive testing capabilities.',
    detailedDescription: 'Super Pancake Automation revolutionizes web testing by leveraging Chrome DevTools Protocol for direct browser communication. This innovative framework eliminates traditional WebDriver overhead, providing blazing-fast test execution with minimal resource consumption. Built with TypeScript and designed for modern development workflows, it offers seamless integration with CI/CD pipelines and supports complex testing scenarios including dynamic content handling, shadow DOM manipulation, and real-time browser state monitoring.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['Chrome DevTools', 'UI Automation', 'JavaScript', 'NPM Package', 'Testing Framework'],
    liveUrl: 'https://www.npmjs.com/package/super-pancake-automation',
    githubUrl: 'https://github.com/pradapjackie/super-pancake-automation',
    featured: true,
    caseStudy: {
      challenge: 'Traditional WebDriver-based automation frameworks were slow, resource-intensive, and prone to flaky tests due to network latency and browser communication overhead.',
      solution: 'Developed a lightweight framework using Chrome DevTools Protocol for direct browser communication, eliminating WebDriver dependencies and reducing test execution time by 60%.',
      results: [
        '60% faster test execution compared to Selenium WebDriver',
        'Reduced memory usage by 40% during test runs',
        'Eliminated network-related test failures',
        'Improved test stability and reliability'
      ],
      impact: 'The framework has been adopted by multiple teams and organizations, resulting in more reliable CI/CD pipelines and faster development cycles. It has become a go-to solution for modern web application testing.'
    },
    metrics: {
      downloads: '5K+',
      stars: '50+',
      version: '2.10.2',
      users: '1K+'
    }
  },
  {
    id: '2',
    title: 'Pakoda Report Playwright',
    description: 'A beautiful custom Playwright reporter with modern UI elements. Version 1.1.7 providing enhanced test reporting capabilities.',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop&crop=center',
    technologies: ['Playwright', 'Test Reporting', 'UI Design', 'NPM Package', 'Custom Reporter'],
    liveUrl: 'https://www.npmjs.com/package/pakoda-report-playwright',
    githubUrl: 'https://github.com/pradapjackie/pakoda-report-playwright',
    featured: true
  },
  {
    id: '3',
    title: 'DOM Automation Framework',
    description: 'A lightweight DOM-based UI automation framework using Chrome DevTools Protocol. Version 1.0.2 for efficient web automation.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['DOM Automation', 'Chrome DevTools', 'Web Automation', 'NPM Package', 'Framework'],
    liveUrl: 'https://www.npmjs.com/package/dom-automation-framework',
    githubUrl: 'https://github.com/pradapjackie/dom-automation-framework',
    featured: true
  },
  {
    id: '4',
    title: 'Cypress Geolocation Locale Faker',
    description: 'Cypress plugin to fake geolocation, timezone, and browser language. Version 1.0.1 for comprehensive testing scenarios.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['Cypress', 'Geolocation', 'Timezone', 'Browser Testing', 'NPM Package'],
    liveUrl: 'https://www.npmjs.com/package/cypress-geolocation-locale-faker',
    githubUrl: 'https://github.com/pradapjackie/cypress-geolocation-locale-faker',
    featured: true
  },
  // Featured GitHub Repositories
  {
    id: '5',
    title: 'Android Native Testing',
    description: 'Kotlin-based Android native automation testing framework with Espresso integration for comprehensive mobile testing.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=300&fit=crop&crop=center',
    technologies: ['Kotlin', 'Android', 'Espresso', 'Mobile Testing', 'Automation'],
    githubUrl: 'https://github.com/pradapjackie/androidNative',
    featured: true
  },
  {
    id: '6',
    title: 'iOS XCUITest Framework',
    description: 'Swift-based iOS automation testing framework using XCUITest for native iOS application testing.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=300&fit=crop&crop=center',
    technologies: ['Swift', 'iOS', 'XCUITest', 'Mobile Testing', 'Automation'],
    githubUrl: 'https://github.com/pradapjackie/ios-xcuitest',
    featured: true
  },
  {
    id: '7',
    title: 'Playwright CircleCI Integration',
    description: 'TypeScript project demonstrating Playwright integration with CircleCI for automated testing in CI/CD pipelines.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['TypeScript', 'Playwright', 'CircleCI', 'CI/CD', 'Automation'],
    githubUrl: 'https://github.com/pradapjackie/playwright-circle-ci',
    featured: true
  },
  {
    id: '8',
    title: 'Performance Testing with JMeter',
    description: 'Comprehensive performance testing repository using Apache JMeter for load testing and performance analysis.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['JMeter', 'Performance Testing', 'Load Testing', 'Java', 'Automation'],
    githubUrl: 'https://github.com/pradapjackie/performance-testing-jmeter',
    featured: true
  },
  {
    id: '9',
    title: 'Flutter Testing Framework',
    description: 'C++ based Flutter testing framework for comprehensive mobile application testing across platforms.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['C++', 'Flutter', 'Mobile Testing', 'Cross-platform', 'Automation'],
    githubUrl: 'https://github.com/pradapjackie/flutter-test',
    featured: true
  },
  {
    id: '10',
    title: 'Date and Time Calculator App',
    description: 'The Ultimate Date & Time Calculator with 50+ Professional Tools! Published on both iOS App Store and Google Play Store with comprehensive date/time calculation features.',
    detailedDescription: 'A comprehensive mobile application offering 50+ professional date and time calculation tools, designed for professionals, developers, and everyday users. The app provides advanced features including time zone conversions, business day calculations, age calculations, countdown timers, and complex date arithmetic. Built with native technologies for optimal performance and user experience across both iOS and Android platforms.',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&crop=center',
    technologies: ['Swift', 'SwiftUI', 'Kotlin', 'Android Studio', 'Core Data', 'iOS', 'Android'],
    liveUrl: 'https://apps.apple.com/app/date-and-time-calculator/id6751056998',
    featured: true,
    caseStudy: {
      challenge: 'Existing date/time calculator apps lacked comprehensive functionality and professional-grade features needed by developers and business professionals.',
      solution: 'Developed a feature-rich mobile application with 50+ calculation tools, implementing native iOS and Android versions with optimized performance and intuitive user interface.',
      results: [
        'Successfully published on both iOS App Store and Google Play Store',
        '50+ professional date/time calculation tools',
        'Cross-platform compatibility with native performance',
        'Intuitive user interface with dark/light mode support'
      ],
      impact: 'The app serves thousands of users worldwide, providing essential date/time calculation tools for professionals, developers, and everyday users. It has received positive reviews for its comprehensive functionality and user-friendly design.'
    },
    metrics: {
      downloads: '10K+',
      version: '1.0.0',
      users: '5K+'
    }
  },
];

export const skills: Skill[] = [
  // Testing & QA
  { name: 'Selenium WebDriver', level: 95, category: 'testing' },
  { name: 'TestNG', level: 90, category: 'testing' },
  { name: 'Cucumber', level: 85, category: 'testing' },
  { name: 'API Testing', level: 92, category: 'testing' },
  { name: 'Mobile Testing', level: 88, category: 'testing' },
  { name: 'Performance Testing', level: 85, category: 'testing' },
  { name: 'Load Testing', level: 80, category: 'testing' },
  { name: 'Security Testing', level: 75, category: 'testing' },
  
  // Frontend
  { name: 'React Native', level: 85, category: 'frontend' },
  { name: 'JavaScript', level: 90, category: 'frontend' },
  { name: 'TypeScript', level: 85, category: 'frontend' },
  { name: 'HTML/CSS', level: 88, category: 'frontend' },
  
  // Backend
  { name: 'Java', level: 90, category: 'backend' },
  { name: 'Python', level: 75, category: 'backend' },
  { name: 'Node.js', level: 80, category: 'backend' },
  { name: 'REST APIs', level: 92, category: 'backend' },
  { name: 'Maven', level: 85, category: 'backend' },
  
  // Mobile
  { name: 'iOS Development', level: 85, category: 'mobile' },
  { name: 'Swift', level: 80, category: 'mobile' },
  { name: 'SwiftUI', level: 75, category: 'mobile' },
  { name: 'Detox E2E', level: 88, category: 'mobile' },
  { name: 'Appium', level: 82, category: 'mobile' },
  
  // Tools
  { name: 'Git/GitLab', level: 90, category: 'tools' },
  { name: 'GitHub', level: 88, category: 'tools' },
  { name: 'Bitbucket', level: 85, category: 'tools' },
  { name: 'Jenkins', level: 85, category: 'tools' },
  { name: 'Docker', level: 80, category: 'tools' },
  { name: 'AWS', level: 75, category: 'tools' },
  { name: 'NPM', level: 85, category: 'tools' },
  { name: 'CI/CD', level: 90, category: 'tools' }
];

export const certifications: Certification[] = [
  {
    name: 'ISTQB Certified Tester',
    issuer: 'International Software Testing Qualifications Board',
    date: 'April 2019',
    url: 'https://www.istqb.org/'
  },
  {
    name: 'Professional Scrum Master I (PSM I)',
    issuer: 'Scrum.org',
    date: 'Recent',
    url: 'https://www.scrum.org/'
  }
];

export const apps: App[] = [
  {
    id: '1',
    name: 'Date and Time Calculator',
    description: 'The Ultimate Date & Time Calculator with 50+ Professional Tools in One App! Transform how you work with dates and times with comprehensive calculation tools designed for professionals, students, and anyone who needs precise date/time calculations.',
    platform: 'iOS & Android',
    technologies: ['Swift', 'SwiftUI', 'Kotlin', 'Android Studio', 'Core Data'],
    status: 'Published',
    downloadUrl: 'https://apps.apple.com/app/date-and-time-calculator/id6751056998',
    iosUrl: 'https://apps.apple.com/app/date-and-time-calculator/id6751056998',
    androidUrl: null,
    icon: '📅',
    features: ['Date Difference Calculator', 'Time Difference Calculator', 'Count Days Tool', 'Add/Subtract Days', 'Birthday Calculator', 'Week Details Calculator', 'Dark & Light Theme', 'Offline Support', 'No Ads', 'Privacy Focused']
  },
  {
    id: '2',
    name: 'Auto Maintenance',
    description: 'Keep Your Vehicle Running Smoothly with Auto Maintenance. Your complete vehicle care companion that helps you track service history, monitor expenses, and never miss important maintenance.',
    platform: 'iOS & Android',
    technologies: ['Swift', 'SwiftUI', 'Kotlin', 'Android Studio', 'Core Data', 'Cloud Sync', 'Firebase'],
    status: 'Published',
    downloadUrl: 'https://play.google.com/store/apps/details?id=com.maintence.car.carmaintence',
    iosUrl: null,
    androidUrl: 'https://play.google.com/store/apps/details?id=com.maintence.car.carmaintence',
    detailPath: '/app/car-maintenance',
    icon: '🚗',
    features: [
      'Comprehensive Maintenance Tracking',
      'Fuel & Expense Management',
      'Smart Reminders',
      'Multiple Vehicle Support',
      'Analytics & Reports',
      'Cloud Sync & Backup',
      'Offline Support',
      'Dark Mode',
      'Data Export (CSV/PDF)',
      'Privacy & Security'
    ],
    detailedInfo: {
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.maintence.car.carmaintence',
      version: 'Latest',
      downloads: '5+',
      rating: '5+',
      contentRating: 'Everyone',
      category: 'Auto & Vehicles',
      developer: 'Pradap Pandian',
      supportEmail: 'pradapjackie@gmail.com',
      phoneNumber: '+971561872072',
      keyFeatures: [
        {
          title: '📊 Comprehensive Maintenance Tracking',
          description: 'Log all service records with dates, costs, and odometer readings. Track oil changes, tire rotations, brake service, and more. Add photos and notes to each maintenance record.',
          icon: '📊'
        },
        {
          title: '⛽ Fuel & Expense Management',
          description: 'Record fuel purchases with cost and quantity. Track all vehicle-related expenses. Monitor spending trends over time. Export data for tax or record-keeping purposes.',
          icon: '⛽'
        },
        {
          title: '🔔 Smart Reminders',
          description: 'Get notifications for upcoming maintenance. Set reminders based on mileage or time intervals. Customize reminder schedules for each vehicle. Never miss an oil change or inspection again.',
          icon: '🔔'
        },
        {
          title: '🚙 Multiple Vehicle Support',
          description: 'Manage unlimited vehicles in one app. Track cars, trucks, motorcycles, RVs, and more. Separate maintenance history for each vehicle. Easy switching between vehicles.',
          icon: '🚙'
        },
        {
          title: '📈 Analytics & Reports',
          description: 'Visualize your maintenance spending. Track fuel economy and costs. See maintenance patterns over time. Identify which services cost the most.',
          icon: '📈'
        },
        {
          title: '☁️ Cloud Sync & Backup',
          description: 'Automatic cloud backup of all your data. Sync across multiple devices. Access your data from anywhere. Never lose your maintenance records.',
          icon: '☁️'
        }
      ],
      premiumFeatures: [
        'Unlimited vehicles (free version: up to 3)',
        'Advanced analytics and reports',
        'Priority customer support',
        'Export data to CSV/PDF',
        'Ad-free experience'
      ],
      benefits: [
        'Catch problems early before they become expensive',
        'Maintain optimal fuel efficiency',
        'Keep warranty coverage valid',
        'Increase your vehicle\'s resale value',
        'Budget effectively for vehicle expenses'
      ],
      privacy: [
        'All data encrypted in transit',
        'Optional account creation with email',
        'Full data export and deletion capabilities',
        'GDPR and CCPA compliant',
        'No ads or third-party data selling'
      ],
      screenshots: [
        'https://play.google.com/store/apps/details?id=com.maintence.car.carmaintence'
      ]
    }
  },
  {
    id: '5',
    name: 'Replio GymFit',
    description: 'Fitness companion for program setup, muscle-based planning, and workout logging. Built for people who want a clear training plan and simple tracking.',
    platform: 'iOS & Android',
    technologies: ['Swift', 'SwiftUI', 'Kotlin', 'Fitness'],
    status: 'Published',
    downloadUrl: null,
    iosUrl: null,
    androidUrl: null,
    supportPath: '/replio-gymfit-support',
    icon: '💪',
    features: ['Program Setup', 'Muscle-based Planning', 'Workout Logging', 'Training History', 'Support']
  },
  {
    id: '3',
    name: 'Shopping Cart App',
    description: 'Smart shopping cart application with price comparison, shopping lists, and budget tracking to help you shop smarter and save money.',
    platform: 'iOS & Android',
    technologies: ['Swift', 'SwiftUI', 'Kotlin', 'Android Studio', 'Core Data'],
    status: 'Coming Soon',
    downloadUrl: null,
    iosUrl: null,
    androidUrl: null,
    icon: '🛒',
    features: ['Shopping Lists', 'Price Comparison', 'Budget Tracking', 'Barcode Scanner', 'Store Locator', 'Deal Alerts', 'Expense Analytics']
  },
  {
    id: '4',
    name: 'Copy Clipboard',
    description: 'Advanced clipboard manager for Mac and Windows with cloud sync, text formatting, and quick access to your copied content across devices.',
    platform: 'Mac & Windows',
    technologies: ['Swift', 'SwiftUI', 'C#', 'WPF', 'CloudKit'],
    status: 'Coming Soon',
    downloadUrl: null,
    iosUrl: null,
    androidUrl: null,
    icon: '📋',
    features: ['Clipboard History', 'Cloud Sync', 'Text Formatting', 'Quick Access', 'Cross-Platform', 'Search & Filter', 'Privacy Protection']
  }
];

export const hobbies: Hobby[] = [
  {
    id: '1',
    name: 'Bike Riding & Motocross Desert',
    description: 'Passionate about motorcycle riding and motocross adventures in desert terrains, exploring challenging off-road trails and enjoying the thrill of desert racing.',
    icon: '🏍️',
    technologies: ['Motorcycle Riding', 'Desert Navigation', 'Off-road Racing', 'Motocross'],
    projects: ['Desert Trail Exploration', 'Motocross Racing', 'Bike Maintenance', 'Adventure Photography']
  },
  {
    id: '2',
    name: 'Content Creator',
    description: 'Creating engaging content across multiple platforms, sharing knowledge about technology, automation, and personal experiences through videos, articles, and social media.',
    icon: '🎬',
    technologies: ['Video Production', 'Content Strategy', 'Social Media', 'Photography', 'Video Editing'],
    projects: ['Tech Tutorials', 'Automation Guides', 'Personal Vlogs', 'Educational Content']
  },
  {
    id: '3',
    name: 'Tamil Car Dudes',
    description: 'Co-founder of Tamil Car Dudes, a premium automotive platform creating comprehensive car reviews, modifications guides, and automotive content in Tamil for car enthusiasts worldwide.',
    icon: '🚗',
    technologies: ['Automotive Journalism', 'Video Production', 'Content Strategy', 'Social Media Marketing', 'Community Building'],
    projects: ['Car Reviews', 'Modification Guides', 'Automotive News', 'Community Events', 'Racing Cafe']
  },
  {
    id: '4',
    name: 'Writing Articles',
    description: 'Technical writer and content creator specializing in software testing, automation frameworks, and technology insights published on Medium and other platforms.',
    icon: '✍️',
    technologies: ['Technical Writing', 'Content Creation', 'Research', 'Publishing'],
    projects: ['Medium Articles', 'Technical Guides', 'Automation Tutorials', 'Industry Insights']
  },
  {
    id: '5',
    name: 'Sports & Fitness',
    description: 'Maintaining an active lifestyle through various sports and fitness activities, focusing on physical health and mental well-being.',
    icon: '💪',
    technologies: ['Fitness Training', 'Sports Analysis', 'Health Tracking', 'Nutrition Planning'],
    projects: ['Personal Fitness', 'Sports Participation', 'Health Monitoring', 'Wellness Planning']
  },
  {
    id: '6',
    name: 'Mobile App Development',
    description: 'Passionate about creating intuitive mobile applications that solve real-world problems, with published apps and upcoming projects.',
    icon: '📱',
    technologies: ['iOS', 'Swift', 'SwiftUI', 'Kotlin', 'Android Studio'],
    projects: ['Date and Time Calculator', 'Car Maintenance App', 'Shopping Cart App', 'Copy Clipboard']
  }
];

export const mediumArticles: MediumArticle[] = [
  {
    id: '1',
    title: 'Mastering Selenium WebDriver: Best Practices for Test Automation',
    description: 'Comprehensive guide covering advanced Selenium techniques, page object model implementation, and robust test automation strategies for enterprise applications.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '8 min read',
    tags: ['Selenium', 'Test Automation', 'Best Practices', 'WebDriver', 'Java'],
    featured: true
  },
  {
    id: '2',
    title: 'Building Scalable Test Automation Frameworks with TestNG',
    description: 'Deep dive into TestNG framework architecture, parallel execution strategies, and creating maintainable test suites for large-scale applications.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '10 min read',
    tags: ['TestNG', 'Test Automation', 'Scalability', 'Framework Design', 'Java'],
    featured: true
  },
  {
    id: '3',
    title: 'React Native E2E Testing with Detox: Complete Guide',
    description: 'Step-by-step tutorial on implementing end-to-end testing for React Native applications using Detox framework, including CI/CD integration.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '12 min read',
    tags: ['React Native', 'Detox', 'E2E Testing', 'Mobile Testing', 'CI/CD'],
    featured: true
  },
  {
    id: '4',
    title: 'Advanced Playwright Testing Strategies for Modern Web Applications',
    description: 'Exploring advanced Playwright techniques including cross-browser testing, visual regression testing, and performance monitoring.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '9 min read',
    tags: ['Playwright', 'Web Testing', 'Cross-browser', 'Visual Testing', 'Performance'],
    featured: true
  },
  {
    id: '5',
    title: 'Mobile App Testing: iOS vs Android Automation Strategies',
    description: 'Comprehensive comparison of iOS and Android testing approaches, covering XCUITest, Espresso, and Appium frameworks.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '11 min read',
    tags: ['Mobile Testing', 'iOS', 'Android', 'XCUITest', 'Espresso', 'Appium'],
    featured: true
  },
  {
    id: '6',
    title: 'Cypress vs Selenium: Choosing the Right Testing Framework',
    description: 'Detailed comparison between Cypress and Selenium WebDriver, covering performance, ease of use, and when to choose each framework.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '7 min read',
    tags: ['Cypress', 'Selenium', 'Framework Comparison', 'Web Testing', 'JavaScript'],
    featured: true
  },
  {
    id: '7',
    title: 'API Testing Best Practices with Postman and REST Assured',
    description: 'Complete guide to API testing covering authentication, data validation, error handling, and automated testing strategies.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '9 min read',
    tags: ['API Testing', 'Postman', 'REST Assured', 'Automation', 'Java'],
    featured: true
  },
  {
    id: '8',
    title: 'Performance Testing with JMeter: Load Testing Strategies',
    description: 'Comprehensive guide to performance testing using Apache JMeter, covering load testing, stress testing, and performance optimization.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '10 min read',
    tags: ['JMeter', 'Performance Testing', 'Load Testing', 'Stress Testing', 'Optimization'],
    featured: true
  },
  {
    id: '9',
    title: 'CI/CD Integration: Automating Test Execution in DevOps Pipelines',
    description: 'Step-by-step guide to integrating automated tests into CI/CD pipelines using Jenkins, GitLab CI, and GitHub Actions.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '11 min read',
    tags: ['CI/CD', 'DevOps', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'Automation'],
    featured: true
  },
  {
    id: '10',
    title: 'Test Data Management: Strategies for Effective Test Automation',
    description: 'Best practices for managing test data in automation frameworks, covering data generation, management, and cleanup strategies.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '8 min read',
    tags: ['Test Data', 'Data Management', 'Automation', 'Best Practices', 'Framework Design'],
    featured: true
  },
  {
    id: '11',
    title: 'Docker for Test Automation: Containerizing Your Test Suites',
    description: 'Complete guide to containerizing test automation frameworks using Docker, covering multi-environment testing and CI/CD integration.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '10 min read',
    tags: ['Docker', 'Containerization', 'Test Automation', 'CI/CD', 'DevOps'],
    featured: true
  },
  {
    id: '12',
    title: 'GitLab CI/CD Pipeline: Advanced Testing Strategies',
    description: 'Advanced GitLab CI/CD pipeline configurations for comprehensive testing workflows, including parallel execution and artifact management.',
    url: 'https://medium.com/@pradappandiyan',
    publishedDate: '2024',
    readTime: '9 min read',
    tags: ['GitLab CI', 'CI/CD', 'Pipeline Configuration', 'Testing Workflows', 'DevOps'],
    featured: true
  }
];

export const contactInfo: ContactInfo = {
  email: 'pradapjackie@gmail.com',
  location: 'Dubai, United Arab Emirates',
  linkedin: 'https://www.linkedin.com/in/pradap-pandiyan/',
  github: 'https://github.com/pradapjackie',
  calendly: 'https://calendly.com/pradappandiyan/30min'
};

// Profile data for QR code and contact cards
export interface ProfileLink {
  name: string;
  url: string;
  icon: string;
}

export const professionalProfile = {
  title: 'Professional',
  description: 'Connect with me for work, collaborations, and professional opportunities.',
  qrPath: '/profile/professional', // QR links to this sub-page with all details
  links: [
    { name: 'LinkedIn', url: contactInfo.linkedin, icon: 'linkedin' },
    { name: 'GitHub', url: contactInfo.github || 'https://github.com/pradapjackie', icon: 'github' },
    { name: 'Email', url: `mailto:${contactInfo.email}`, icon: 'email' },
    { name: 'Calendly', url: contactInfo.calendly || '', icon: 'calendar' },
    { name: 'Medium', url: 'https://pradappandiyan.medium.com/', icon: 'medium' },
    { name: 'NPM', url: 'https://www.npmjs.com/~pradapjackie', icon: 'npm' },
    { name: 'GitLab', url: 'https://gitlab.com/pradapjackie', icon: 'gitlab' },
    { name: 'Bitbucket', url: 'https://bitbucket.org/pradapjackie/workspace/repositories/', icon: 'bitbucket' }
  ] as ProfileLink[]
};

export const personalProfile = {
  title: 'Personal',
  description: 'Follow my hobbies, content creation, and personal projects.',
  qrPath: '/profile/personal', // QR links to this sub-page with all details
  links: [
    { name: 'YouTube', url: 'https://www.youtube.com/@TheMotoTriangle', icon: 'youtube' },
    { name: 'Tamil Car Dudes', url: 'https://www.tamilcardudes.com/', icon: 'car' }
    // Add Instagram when you have a profile: { name: 'Instagram', url: 'https://www.instagram.com/yourhandle', icon: 'instagram' }
  ] as ProfileLink[]
};
